import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import adminApi from "../../../api/adminApi";
import { isBcRole } from "../../../api/types/bcTypes";
import useHideLeftSideRootMenu from "../../../hooks/useHideLeftSideRootMenu";
import { parseBusinessCentralUrl } from "../../../utilities/navigationHelper";

const getJobStatus = withErrorHandling(adminApi.getBusinessCentralAccessJobStatus);

const maxAttempts = 16;

const BusinessCentralRedirectPage = () => {
  const [searchParams] = useSearchParams();

  useHideLeftSideRootMenu();

  const [isLongWait, setIsLongWait] = useState(false);

  const role = useMemo(() => {
    const roleParam = searchParams.get("role");
    return isBcRole(roleParam) ? roleParam : undefined;
  }, [searchParams]);

  const redirectUrl = parseBusinessCentralUrl(searchParams.get("redirectUrl"));

  const redirect = useCallback(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [redirectUrl]);

  const ensureAccess = useCallback(() => adminApi.ensureBusinessCentralAccessIsEnabled(role), [role]);

  const [accessResp, accessError] = useFetch(ensureAccess);

  const checkAccessAndRedirect = useCallback(async () => {
    if (accessResp === undefined) {
      return;
    }

    if (!accessResp.jobStarted || !accessResp.jobId) {
      redirect();
      return;
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await wait(attempt * 1000);

      const [jobResp, jobError] = await getJobStatus(accessResp.jobId);
      if (jobError) {
        logError(jobError, "[BusinessCentralRedirectPage] getJobStatus");
        redirect();
        return;
      }

      if (jobResp.finished) {
        redirect();
        return;
      }

      if (attempt > maxAttempts / 2) {
        setIsLongWait(true);
      }
    }

    redirect();
  }, [accessResp, redirect]);

  useEffect(() => {
    checkAccessAndRedirect();
  }, [checkAccessAndRedirect]);

  if (!redirectUrl) {
    return <Navigate to="/" />;
  }

  if (accessError) {
    logError(accessError, "[BusinessCentralRedirectPage] ensureAccess");
    redirect();
  }

  const redirectMessage =
    role === "EAC Corporate Accountant"
      ? "You are being redirected to Corporate Accounting"
      : "You are being redirected to Fund Operations";

  const suffix = isLongWait ? ". Access is still being prepared..." : "...";

  return <FullScreenLoader title="Please wait" subtitle={redirectMessage + suffix} />;
};

export default BusinessCentralRedirectPage;
